import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import ImportModal from '../components/ImportModal';
import { useCompany } from '../context/CompanyContext';
import api from '../api';
import './Dashboard.css';

const EMPTY_FORM = {
  name_of_person: '',
  pan: '',
  email: '',
  mobile: '',
  dsc_making_date: '',
  dsc_expiry_date: '',
};

const STATUS_PILL = {
  Active: 'dash-pill-green',
  'Expiring in 30 days': 'dash-pill-amber',
  Expired: 'dash-pill-red',
};

function DscRegister() {
  const { selectedCompanyId } = useCompany();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const refresh = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/dsc-register', { params: { company_id: selectedCompanyId } }).then((res) => setRows(res.data));
  };

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name_of_person: r.name_of_person || '',
      pan: r.pan || '',
      email: r.email || '',
      mobile: r.mobile || '',
      dsc_making_date: r.dsc_making_date || '',
      dsc_expiry_date: r.dsc_expiry_date || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    setSaving(true);
    const payload = {
      ...form,
      company_id: selectedCompanyId,
      dsc_making_date: form.dsc_making_date || null,
      dsc_expiry_date: form.dsc_expiry_date || null,
    };
    const request = editingId ? api.put(`/dsc-register/${editingId}`, payload) : api.post('/dsc-register', payload);
    request
      .then(() => {
        refresh();
        showToast(editingId ? 'DSC record updated ✓' : 'DSC record added ✓');
        setModalOpen(false);
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    api.delete(`/dsc-register/${deleteTarget.id}`).then(() => {
      refresh();
      showToast(`${deleteTarget.name_of_person} deleted`);
      setDeleteTarget(null);
    });
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">DSC Register</h1>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="dash-card">
        <div className="page-title-row">
          <h2 className="dash-card-title">Digital Signature Certificates</h2>
          <div className="table-actions">
            <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
              Import
            </button>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAdd}>
              + Add New DSC
            </button>
          </div>
        </div>

        <table className="dash-table">
          <thead>
            <tr>
              <th>Name of Person</th>
              <th>PAN</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Making Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td className="dash-empty" colSpan={8}>No DSC records yet. Add your first one.</td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name_of_person}</td>
                <td>{r.pan || '—'}</td>
                <td>{r.email || '—'}</td>
                <td>{r.mobile || '—'}</td>
                <td>{r.dsc_making_date || '—'}</td>
                <td>{r.dsc_expiry_date || '—'}</td>
                <td>
                  <span className={`dash-pill ${STATUS_PILL[r.status] || 'dash-pill-blue'}`}>{r.status}</span>
                </td>
                <td>
                  <div className="table-actions">
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEdit(r)}>
                      Edit
                    </button>
                    <button className="dash-btn-red-outline" type="button" onClick={() => setDeleteTarget(r)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingId ? 'Edit DSC Record' : 'Add New DSC'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="field-group">
                <span className="field-label">Name of Person *</span>
                <input
                  className="field-input"
                  required
                  value={form.name_of_person}
                  onChange={(e) => setForm({ ...form, name_of_person: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">PAN</span>
                <input
                  className="field-input"
                  value={form.pan}
                  onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                  maxLength={10}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Email</span>
                  <input
                    className="field-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">Mobile</span>
                  <input
                    className="field-input"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">DSC Making Date</span>
                  <input
                    type="date"
                    className="field-input"
                    value={form.dsc_making_date || ''}
                    onChange={(e) => setForm({ ...form, dsc_making_date: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">DSC Expiry Date</span>
                  <input
                    type="date"
                    className="field-input"
                    value={form.dsc_expiry_date || ''}
                    onChange={(e) => setForm({ ...form, dsc_expiry_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete DSC Record</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete <strong>{deleteTarget.name_of_person}</strong>'s DSC record? This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="dsc"
        moduleLabel="DSC Register"
        companyId={selectedCompanyId}
        onImported={refresh}
        supportsCreateNew
      />
    </PageLayout>
  );
}

export default DscRegister;
